import { Button, Checkbox, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";
import { templateConfigsColumnsData } from "shared/constants";
import { ReportItem } from "shared/api";
import { renderIsDefaultString } from "shared/helpers";
import { TableButton, TableSearchFilter } from "shared/ui";
import { FilterFilled } from "@ant-design/icons";
import classNames from "classnames";

const { name, templateId, reportType, reportCategory, isDefault, actions } =
  templateConfigsColumnsData;

interface UseTableConfigColumnsProps {
  t: TFunction;
  handleEditReport: (record: ReportItem) => void;
  showDeleteReportModal: (record: ReportItem) => void;
  selectedNames: string[];
  nameOptions: {
    label: string;
    value: string;
  }[];
  setSelectedNames: React.Dispatch<React.SetStateAction<string[]>>;
  templateIdValue: string;
  handleTemplateIdChange: (
    e: string | React.ChangeEvent<HTMLInputElement>
  ) => void;
  clearTemplateIdValue: () => void;
  selectedReportTypes: string[];
  setSelectedReportTypes: React.Dispatch<React.SetStateAction<string[]>>;
  reportCategoryValue: string;
  handleReportCategoryChange: (
    e: string | React.ChangeEvent<HTMLInputElement>
  ) => void;
  clearReportCategoryValue: () => void;

  isDefaultValue: boolean | null;
  setIsDefaultValue: React.Dispatch<React.SetStateAction<boolean | null>>;
}

export const useTableConfigColumns = ({
  t,
  handleEditReport,
  showDeleteReportModal,
  nameOptions,
  clearTemplateIdValue,
  handleTemplateIdChange,
  selectedNames,
  setSelectedNames,
  templateIdValue,
  selectedReportTypes,
  setSelectedReportTypes,
  clearReportCategoryValue,
  handleReportCategoryChange,
  isDefaultValue,
  reportCategoryValue,
  setIsDefaultValue,
}: UseTableConfigColumnsProps): ColumnsType<ReportItem> => [
  {
    title: t(name.title),
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": selectedNames.length > 0,
        })}
      />
    ),
    filterDropdown: () => (
      <div style={{ padding: 8, width: 250 }}>
        <Select
          mode="multiple"
          allowClear
          showSearch
          maxTagCount="responsive"
          placeholder={t("Select report names")}
          options={nameOptions}
          value={selectedNames}
          onChange={(vals) => setSelectedNames(vals)}
          style={{ width: "100%" }}
        />
      </div>
    ),
    render: (report: ReportItem) => (
      <Button
        onClick={() => handleEditReport(report)}
        className="border-none hover:border-none bg-transparent shadow-none"
      >
        <span className="underline"> {report?.name}</span>{" "}
      </Button>
    ),
  },

  {
    title: t(templateId.title),
    filterIcon: (
      <FilterFilled
        className={
          templateIdValue.length ? "!text-blue-600  scale-125" : undefined
        }
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearTemplateIdValue}
        confirm={confirm}
        value={templateIdValue!}
        handleFilterChange={handleTemplateIdChange!}
        visible={visible}
        placeholder={t("Enter the template id")!}
        title={t("template id")!}
      />
    ),
    render: (report: ReportItem) => report?.template_id ?? "-",
  },

  {
    title: t(reportType.title),
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": selectedReportTypes.length > 0,
        })}
      />
    ),
    filterDropdown: () => {
      const reportTypeOptions = [
        "DRIGGING_RIGG",
        "MANAGEMENT",
        "MATERIAL_ITEM",
        "MULTIPLE_MATERIAL",
        "MULTIPLE_MATERIAL_ITEM",
        "MULTIPLE_ORDER",
        "MULTIPLE_PACKAGING",
        "MULTIPLE_QUALITY",
        "ORDER",
        "PACKAGING",
        "REEL_LAB_ARCHIVE",
        "MULTIPLE_COMPANY_PACKAGING",
      ].map((type) => ({
        label: type.toLowerCase(),
        value: type,
      }));

      return (
        <div style={{ padding: 8, width: 250 }}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder={t("Select report types")}
            options={reportTypeOptions}
            value={selectedReportTypes}
            onChange={(vals) => setSelectedReportTypes(vals)}
            style={{ width: "100%" }}
            maxTagCount="responsive"
          />
        </div>
      );
    },
    render: (report: ReportItem) => report?.report_type ?? "-",
  },

  {
    title: t(reportCategory.title),
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": reportCategoryValue.length > 0,
        })}
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearReportCategoryValue}
        confirm={confirm}
        value={reportCategoryValue}
        handleFilterChange={handleReportCategoryChange}
        visible={visible}
        placeholder={t("Enter category name") || ""}
        title={t("Filter by Category") || ""}
      />
    ),
    render: (report: ReportItem) => report?.report_category?.name ?? "-",
  },
  {
    title: t(isDefault.title),
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-green-600 scale-125": isDefaultValue !== null,
        })}
      />
    ),
    filterDropdown: () => (
      <div style={{ padding: 8 }}>
        <Checkbox
          checked={isDefaultValue === true}
          onChange={(e) => setIsDefaultValue(e.target.checked ? true : null)}
        >
          {t("Show only default")}
        </Checkbox>

        <Checkbox
          checked={isDefaultValue === false}
          onChange={(e) => setIsDefaultValue(e.target.checked ? false : null)}
        >
          {t("Show only non-default")}
        </Checkbox>

        {isDefaultValue !== null && (
          <Button
            type="link"
            size="small"
            onClick={() => setIsDefaultValue(null)}
            style={{ marginTop: 6, padding: 0 }}
          >
            {t("Reset filter")}
          </Button>
        )}
      </div>
    ),
    render: (report: ReportItem) => renderIsDefaultString(report),
  },
  {
    title: t(actions.title),
    render: (report: ReportItem) => (
      <TableButton
        type="delete"
        onClick={() => showDeleteReportModal(report)}
      />
    ),
  },
];
